import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { QrCode, Users, MessageCircle, Shield, AlertTriangle } from 'lucide-react';
import { TeamAccessCodeGenerator } from './TeamAccessCodeGenerator';
import { Under13TeamPage } from './Under13TeamPage';
import { useAuth } from '@/hooks/useAuth';

interface Under13TeamManagerProps {
  teamId: number;
  teamName: string;
  teamAgeGroup?: string;
}

interface TeamMember {
  id: string;
  user_id: string;
  joined_at: string;
  profiles?: {
    username?: string;
    first_name?: string;
    age_group?: string;
  };
}

interface TeamAccessCode {
  id: string;
  access_code: string;
  is_active: boolean;
  current_uses: number;
  max_uses?: number;
  expires_at?: string;
  created_at: string;
}

export const Under13TeamManager: React.FC<Under13TeamManagerProps> = ({ 
  teamId, 
  teamName, 
  teamAgeGroup 
}) => {
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [accessCodes, setAccessCodes] = useState<TeamAccessCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'overview' | 'generator' | 'team-space'>('overview');

  // Check if this is a 13 and under team
  const isUnder13Team = teamAgeGroup && (
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '13-and-under', '13 and under'].includes(teamAgeGroup) ||
    teamAgeGroup.toLowerCase().includes('13') && teamAgeGroup.toLowerCase().includes('under')
  );

  useEffect(() => {
    if (isUnder13Team) {
      loadTeamData();
    }
  }, [teamId, isUnder13Team]);

  const loadTeamData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTeamMembers(),
        loadAccessCodes()
      ]);
    } catch (error) {
      console.error('Error loading team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('team_players')
        .select('user_id, joined_at')
        .eq('team_id', teamId);

      if (error) throw error;
      
      // Transform data to match TeamMember interface
      const transformedData = (data || []).map(item => ({
        id: `${item.user_id}-${teamId}`, // Create unique ID
        user_id: item.user_id,
        joined_at: item.joined_at,
        profiles: {
          username: 'Team Member',
          first_name: 'Team Member',
          age_group: 'Unknown'
        }
      }));
      
      setTeamMembers(transformedData);
    } catch (error: any) {
      console.error('Error loading team members:', error);
      toast.error('Failed to load team members');
    }
  };

  const loadAccessCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('team_access_codes')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccessCodes(data || []);
    } catch (error: any) {
      console.error('Error loading access codes:', error);
      toast.error('Failed to load access codes');
    }
  };

  const deactivateCode = async (codeId: string) => {
    try {
      const { error } = await supabase
        .from('team_access_codes')
        .update({ is_active: false })
        .eq('id', codeId);

      if (error) throw error;
      
      toast.success('Access code deactivated');
      await loadAccessCodes();
    } catch (error: any) {
      console.error('Error deactivating code:', error);
      toast.error('Failed to deactivate code');
    }
  };

  const removeMember = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this member from the team?')) return;

    try {
      const { error } = await supabase
        .from('team_players')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', userId);

      if (error) throw error;
      
      toast.success('Member removed from team');
      await loadTeamMembers();
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  if (!isUnder13Team) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Team Access Management
          </CardTitle>
          <CardDescription>
            Access code management is only available for teams with players 13 and under
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Under 13 Team Management
          </CardTitle>
          <CardDescription>
            Secure team access management for young players
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-6">
            <Button 
              variant={activeView === 'overview' ? 'default' : 'outline'}
              onClick={() => setActiveView('overview')}
            >
              <Users className="h-4 w-4 mr-2" />
              Overview
            </Button>
            <Button 
              variant={activeView === 'generator' ? 'default' : 'outline'}
              onClick={() => setActiveView('generator')}
            >
              <QrCode className="h-4 w-4 mr-2" />
              Generate Code
            </Button>
            <Button 
              variant={activeView === 'team-space' ? 'default' : 'outline'}
              onClick={() => setActiveView('team-space')}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Team Space
            </Button>
          </div>

          {activeView === 'overview' && (
            <div className="space-y-6">
              {/* Security Notice */}
              <div className="flex items-start gap-3 p-4 bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-orange-800 dark:text-orange-200">
                    Security Notice for Under 13 Teams
                  </h4>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                    All team members must use a valid team access code to join. 
                    Team chat and content sharing is restricted to verified team members only.
                  </p>
                </div>
              </div>

              {/* Active Access Codes */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Active Access Codes</h3>
                {accessCodes.filter(code => code.is_active).map((code) => (
                  <Card key={code.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-mono font-bold text-lg">{code.access_code}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">
                              Uses: {code.current_uses}{code.max_uses ? `/${code.max_uses}` : '/∞'}
                            </Badge>
                            {code.expires_at && (
                              <Badge variant="outline">
                                Expires: {new Date(code.expires_at).toLocaleDateString()}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => deactivateCode(code.id)}
                        >
                          Deactivate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {accessCodes.filter(code => code.is_active).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">
                    No active access codes. Generate one to allow new members to join.
                  </p>
                )}
              </div>

              {/* Team Members */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Team Members ({teamMembers.length})</h3>
                {teamMembers.map((member) => (
                  <Card key={member.id}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                            <Users className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-semibold">
                              {member.profiles?.username || member.profiles?.first_name || 'Team Member'}
                            </p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs">
                                Age: {member.profiles?.age_group || 'Not specified'}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                Joined: {new Date(member.joined_at).toLocaleDateString()}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => removeMember(member.user_id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {teamMembers.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">
                    No team members yet. Share your access code to invite players!
                  </p>
                )}
              </div>
            </div>
          )}

          {activeView === 'generator' && (
            <TeamAccessCodeGenerator teamId={teamId} teamName={teamName} />
          )}

          {activeView === 'team-space' && (
            <Under13TeamPage teamId={teamId} teamName={teamName} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};